import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { MaskLine } from "./Reveal";
import { BRAND, IMAGES, TID } from "../lib/brand";
import { API } from "../lib/api";

// Convert a stored URL (/api/hero/media/... or absolute) into a fully qualified src.
const resolveMediaUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  if (u.startsWith("/api/")) return `${API.replace("/api", "")}${u}`;
  return u;
};

// Fallback slide if the API returns nothing (e.g. first paint / offline).
const FALLBACK_SLIDE = {
  id: "fallback",
  title: "R34 · Union Station",
  desktop_media_url: IMAGES.hero,
  mobile_media_url: IMAGES.heroMobile,
  alt_text: "Tokyo-night automotive atmosphere — temporary cinematic imagery.",
  focal_desktop_x: 50, focal_desktop_y: 50,
  focal_mobile_x: 50, focal_mobile_y: 50,
  overlay_darkness: 35, gradient_left: 78, gradient_right: 16, gradient_bottom: 58,
  red_accent: 55, blue_accent: 12,
  duration_ms: 7000,
};

const usePrefersReducedMotion = () => {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const on = () => setReduce(mq.matches);
    on();
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  return reduce;
};

const useIsMobile = () => {
  const [m, setM] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const on = () => setM(mq.matches);
    on();
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  return m;
};

// One slide layer: a single full-bleed <img> using object-fit: cover with focal-point crop.
// NO Ken Burns, NO pointer parallax, NO scroll parallax — image sits at true 1× cover so
// high-res source photos stay crisp and there's zero scroll jank.
function SlideImage({ slide, isMobile }) {
  const url = isMobile ? (slide.mobile_media_url || slide.desktop_media_url) : slide.desktop_media_url;
  const fx = isMobile ? slide.focal_mobile_x : slide.focal_desktop_x;
  const fy = isMobile ? slide.focal_mobile_y : slide.focal_desktop_y;

  return (
    <img
      src={resolveMediaUrl(url)}
      alt={slide.alt_text || slide.title || ""}
      className="absolute inset-0 w-full h-full object-cover select-none"
      style={{ objectPosition: `${fx}% ${fy}%` }}
      loading="eager"
      decoding="async"
      draggable={false}
      aria-hidden="true"
    />
  );
}

// Directional overlays (per-slide tunable) — softened so the underlying photo stays vivid.
function SlideOverlay({ slide }) {
  const dark = (slide.overlay_darkness ?? 40) / 100;
  const gl = (slide.gradient_left ?? 78) / 100;
  const gr = (slide.gradient_right ?? 18) / 100;
  const gb = (slide.gradient_bottom ?? 58) / 100;
  const red = (slide.red_accent ?? 55) / 100;
  const blue = (slide.blue_accent ?? 12) / 100;

  return (
    <>
      {/* Left-side scrim behind text — keeps typography readable but doesn't wash the vehicle side. */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `linear-gradient(90deg, rgba(5,5,7,${gl}) 0%, rgba(5,5,7,${Math.max(gl - 0.4, 0.06)}) 30%, rgba(5,5,7,0.05) 60%, rgba(5,5,7,${gr * 0.8}) 100%)`,
        }}
      />
      {/* Top nav gradient */}
      <div
        className="absolute inset-x-0 top-0 h-40 pointer-events-none"
        style={{
          background: `linear-gradient(180deg, rgba(5,5,7,${Math.max(0.5, dark * 0.9)}) 0%, rgba(5,5,7,0) 100%)`,
        }}
      />
      {/* Bottom seamless transition to next section */}
      <div
        className="absolute inset-x-0 bottom-0 h-44 pointer-events-none"
        style={{
          background: `linear-gradient(180deg, rgba(5,5,7,0) 0%, rgba(5,5,7,${gb}) 60%, rgba(5,5,7,1) 100%)`,
        }}
      />
      {/* Base darkness lift — very subtle */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: `rgba(5,5,7,${dark * 0.15})` }}
      />
      {/* Red reflected-light accent — ambient, not a hot spot */}
      <div
        className="absolute inset-0 pointer-events-none mix-blend-screen"
        style={{
          background: `radial-gradient(30vmin 24vmin at 82% 18%, rgba(242,27,35,${red * 0.22}) 0%, rgba(242,27,35,0) 70%)`,
        }}
      />
      {/* Cool blue/violet fill */}
      <div
        className="absolute inset-0 pointer-events-none mix-blend-screen"
        style={{
          background: `radial-gradient(45vmin 30vmin at 8% 90%, rgba(72,88,142,${blue * 0.35}) 0%, rgba(72,88,142,0) 70%)`,
        }}
      />
    </>
  );
}

export default function HeroSlider() {
  const [slides, setSlides] = useState([FALLBACK_SLIDE]);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [visible, setVisible] = useState(true);
  const sectionRef = useRef(null);
  const timerRef = useRef(null);
  const touchStartX = useRef(null);
  const resumeTimeoutRef = useRef(null);

  const reduce = usePrefersReducedMotion();
  const isMobile = useIsMobile();

  // Fetch published slides once
  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/hero/slides`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        if (cancelled) return;
        if (Array.isArray(data) && data.length > 0) setSlides(data);
      })
      .catch(() => { /* keep fallback */ });
    return () => { cancelled = true; };
  }, []);

  const active = slides[idx] || FALLBACK_SLIDE;
  const next = useCallback(() => setIdx((i) => (i + 1) % slides.length), [slides.length]);
  const prev = useCallback(() => setIdx((i) => (i - 1 + slides.length) % slides.length), [slides.length]);
  const goTo = useCallback((i) => setIdx(((i % slides.length) + slides.length) % slides.length), [slides.length]);

  // Autoplay — respects reduced-motion, pause on tab-hidden, off-screen, or user interaction.
  // (No mouse-hover pause — the hero fills the viewport, so hover would be always-on for desktop.)
  useEffect(() => {
    if (reduce) return;
    if (slides.length <= 1) return;
    if (paused || !visible) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    const dur = Math.max(3000, active.duration_ms || 7000);
    timerRef.current = setTimeout(next, dur);
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [idx, paused, visible, active.duration_ms, next, reduce, slides.length]);

  // Prefetch next slide's images
  useEffect(() => {
    if (slides.length <= 1) return;
    const nxt = slides[(idx + 1) % slides.length];
    if (!nxt) return;
    const urls = [nxt.desktop_media_url, nxt.mobile_media_url].filter(Boolean);
    urls.forEach((u) => { const i = new Image(); i.src = resolveMediaUrl(u); });
  }, [idx, slides]);

  // Pause when tab hidden
  useEffect(() => {
    const on = () => setVisible(!document.hidden);
    document.addEventListener("visibilitychange", on);
    return () => document.removeEventListener("visibilitychange", on);
  }, []);

  // Pause when hero scrolls off-screen (IntersectionObserver)
  useEffect(() => {
    if (!sectionRef.current) return;
    const io = new IntersectionObserver(([e]) => setVisible(e.isIntersecting), { threshold: 0.12 });
    io.observe(sectionRef.current);
    return () => io.disconnect();
  }, []);

  // Auto-resume after user interaction (10s delay)
  const bumpResume = () => {
    if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
    setPaused(true);
    resumeTimeoutRef.current = setTimeout(() => setPaused(false), 10000);
  };

  // Keyboard nav (only when hero has focus or is in viewport top)
  useEffect(() => {
    const onKey = (e) => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const inView = rect.top < window.innerHeight * 0.6 && rect.bottom > 0;
      if (!inView) return;
      if (e.key === "ArrowRight") { next(); bumpResume(); }
      else if (e.key === "ArrowLeft") { prev(); bumpResume(); }
      else if (e.key === " " || e.code === "Space") {
        if (["INPUT","TEXTAREA"].includes(document.activeElement?.tagName)) return;
        e.preventDefault();
        setPaused((p) => !p);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev]);

  // Swipe gestures (mobile)
  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) > 45) { dx < 0 ? next() : prev(); bumpResume(); }
  };

  const hasMultiple = slides.length > 1;

  // Simple crossfade only — no scale, no clip-path, no filter. Zero jank, zero pixelation.
  const fade = {
    initial: { opacity: 0 },
    animate: { opacity: 1, transition: { duration: 1.0, ease: [0.22, 1, 0.36, 1] } },
    exit:    { opacity: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
  };

  return (
    <section
      ref={sectionRef}
      data-testid={TID.hero.root}
      className="relative w-screen min-h-[62svh] md:min-h-[68svh] overflow-hidden"
      style={{ marginLeft: "calc(50% - 50vw)", marginRight: "calc(50% - 50vw)" }}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      role="region"
      aria-roledescription="Cinematic hero slider"
      aria-label="ProForm hero slider"
    >
      {/* SLIDE STACK — simple crossfade */}
      <div className="absolute inset-0 z-0 bg-ink-950">
        <AnimatePresence mode="sync">
          <motion.div
            key={active.id}
            initial={fade.initial}
            animate={fade.animate}
            exit={fade.exit}
            className="absolute inset-0"
            aria-hidden
          >
            <SlideImage slide={active} isMobile={isMobile} />
            <SlideOverlay slide={active} />
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Sun-drift bloom (echoes ProForm rising sun) — static ambient light */}
      <div
        className="pointer-events-none absolute z-[1] top-[12%] right-[6%] w-[36vmin] h-[36vmin] rounded-full bg-proform-red opacity-40 blur-3xl animate-sun-drift"
        aria-hidden
      />

      {/* CONTENT — stable across slide changes */}
      <div className="relative z-10 wrap pt-24 md:pt-32 pb-20 md:pb-24 min-h-[62svh] md:min-h-[68svh] flex flex-col justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.6 }}
          className="flex items-center gap-4 mb-8 md:mb-12"
        >
          <span className="h-px w-10 bg-proform-red" />
          <span className="label">Denver · Colorado · Since {BRAND.founded}</span>
        </motion.div>

        <h1 className="font-display uppercase tracking-tighter leading-[0.85] text-chrome-100 text-[12vw] md:text-[6vw] max-w-[92vw] md:max-w-[56vw] drop-shadow-[0_4px_24px_rgba(0,0,0,0.55)]">
          <MaskLine delay={0.1} duration={1.0}>
            <span className="inline-block">Built</span>{" "}
            <span className="inline-block text-proform-red">Different.</span>
          </MaskLine>
          <MaskLine delay={0.3} duration={1.0}>
            <span className="inline-block">Finished Right.</span>
          </MaskLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 max-w-xl text-chrome-100/90 text-base md:text-lg leading-relaxed drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]"
        >
          Precision collision repair, custom refinishing, and specialist bodywork for
          JDM, performance, race, drift &amp; everyday vehicles across the Colorado Front Range.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.9 }}
          className="mt-10 flex flex-col gap-3"
        >
          <div className="flex flex-wrap items-center gap-3">
            <Link to="/estimate" data-testid={TID.hero.ctaEstimate} className="btn btn-primary">
              Snap it. Send it. We&apos;ll Quote it. <span aria-hidden>→</span>
            </Link>
            <a href={`tel:${BRAND.phoneRaw}`} className="btn btn-ghost backdrop-blur-sm">
              Call or Text — {BRAND.phoneDisplay}
            </a>
            <a href="#manifesto" data-testid={TID.hero.ctaExplore} className="hidden md:inline-flex btn btn-ghost backdrop-blur-sm">
              Explore ProForm
            </a>
          </div>
          <p className="font-mono text-[11px] tracking-[0.22em] uppercase text-chrome-300 max-w-xl">
            Free · No shop visit required for a preliminary review · Insurance &amp; custom JDM welcome
          </p>
        </motion.div>
      </div>

      {/* Bottom fade */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-ink-950 -z-[5]" />

      {/* CONTROLS */}
      {hasMultiple && !reduce && (
        <>
          {/* Bottom-left: counter + progress ticks (desktop) / compact counter (mobile) */}
          <div className="absolute bottom-24 md:bottom-10 left-6 md:left-10 z-20 flex items-center gap-4">
            <span className="font-mono text-[11px] tracking-[0.24em] text-chrome-100" data-testid="hero-slide-counter">
              {String(idx + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")}
            </span>
            <div className="hidden md:flex items-center gap-2" role="tablist" aria-label="Slide indicators">
              {slides.map((s, i) => (
                <button
                  key={s.id || i}
                  type="button"
                  onClick={() => { goTo(i); bumpResume(); }}
                  aria-label={`Go to slide ${i + 1}`}
                  aria-current={i === idx}
                  data-testid={`hero-indicator-${i}`}
                  className={`h-[2px] transition-all duration-500 ${i === idx ? "w-10 bg-proform-red" : "w-6 bg-chrome-100/30 hover:bg-chrome-100/60"}`}
                />
              ))}
            </div>
          </div>

          {/* Bottom-right: prev/next + pause */}
          <div className="absolute bottom-24 md:bottom-10 right-6 md:right-10 z-20 flex items-center gap-2">
            <button
              type="button"
              onClick={() => { setPaused((p) => !p); }}
              aria-label={paused ? "Play slider" : "Pause slider"}
              data-testid="hero-play-pause"
              className="h-9 w-9 flex items-center justify-center border border-chrome-100/30 hover:border-proform-red hover:text-proform-red transition-colors text-chrome-100"
            >
              {paused ? (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
              ) : (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M6 5h4v14H6zM14 5h4v14h-4z" /></svg>
              )}
            </button>
            <button
              type="button"
              onClick={() => { prev(); bumpResume(); }}
              aria-label="Previous slide"
              data-testid="hero-prev"
              className="h-9 w-9 flex items-center justify-center border border-chrome-100/30 hover:border-proform-red hover:text-proform-red transition-colors text-chrome-100"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 6l-6 6 6 6" /></svg>
            </button>
            <button
              type="button"
              onClick={() => { next(); bumpResume(); }}
              aria-label="Next slide"
              data-testid="hero-next"
              className="h-9 w-9 flex items-center justify-center border border-chrome-100/30 hover:border-proform-red hover:text-proform-red transition-colors text-chrome-100"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 6l6 6-6 6" /></svg>
            </button>
          </div>
        </>
      )}

      {/* Scroll hint (kept for continuity) */}
      <a
        href="#manifesto"
        data-testid={TID.hero.scrollHint}
        className="absolute left-1/2 -translate-x-1/2 bottom-8 md:bottom-24 hidden md:flex flex-col items-center gap-3 label z-10 opacity-70 hover:opacity-100 transition-opacity"
        aria-label="Scroll to next section"
      >
        <span>Scroll</span>
        <span className="block w-px h-8 bg-chrome-100 origin-top animate-pulse" />
      </a>
    </section>
  );
}
